import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  commitBankMatches,
  commitImport,
  getOnboardingStatus,
  getOnboardingIntegrations,
  previewBankStatement,
  previewImport,
  type BankCommitInput,
  type BankCommitResult,
  type BankPreview,
  type ImportCommitResult,
  type ImportPreview,
} from '../lib/onboardingApi';

// React Query hooks for the onboarding wizard (CLE-131).

const ONBOARDING_KEY = ['onboarding'] as const;

export function useOnboardingStatus() {
  return useQuery({
    queryKey: [...ONBOARDING_KEY, 'status'],
    queryFn: getOnboardingStatus,
  });
}

// Dependency / integration report (CLE-130). `probe` triggers a live SmartBill
// connectivity check on the backend, so it is opt-in (default off).
export function useOnboardingIntegrations(probe = false) {
  return useQuery({
    queryKey: [...ONBOARDING_KEY, 'integrations', probe],
    queryFn: () => getOnboardingIntegrations(probe),
  });
}

export function useInvalidateOnboardingStatus() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [...ONBOARDING_KEY, 'status'] });
}

export function usePreviewImport() {
  return useMutation<ImportPreview, Error, File>({
    mutationFn: (file) => previewImport(file),
  });
}

export function useCommitImport() {
  const qc = useQueryClient();
  return useMutation<ImportCommitResult, Error, File>({
    mutationFn: (file) => commitImport(file),
    // A successful import changes the step booleans behind /status.
    onSuccess: () => qc.invalidateQueries({ queryKey: [...ONBOARDING_KEY, 'status'] }),
  });
}

export function usePreviewBankStatement() {
  return useMutation<BankPreview, Error, File>({
    mutationFn: (file) => previewBankStatement(file),
  });
}

export function useCommitBankMatches() {
  const qc = useQueryClient();
  return useMutation<BankCommitResult, Error, BankCommitInput>({
    mutationFn: (input) => commitBankMatches(input),
    // Marking invoices paid + recording an import flips the bank step behind /status.
    onSuccess: () => qc.invalidateQueries({ queryKey: [...ONBOARDING_KEY, 'status'] }),
  });
}
